<script setup lang="ts">
import { ref, computed } from 'vue'
import uploadService from '@/services/ide/upload.service'
import { useIdeStore } from '@/stores/ide.store'
import { FILE_MESSAGE_TYPE } from '@/utils/ide'
import { IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import uploadFilesIcon from '@/assets/images/ide/upload.svg'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.BOTTOM
  }
})

const ideStore = useIdeStore()

const uploadInput = ref<HTMLElement | null>(null)
const inputKey = ref<number>(0)

const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})
const isHtml = computed(() => {
  return ideStore.isHtml
})
const isFileMessage = computed(() => {
  return ideStore.isFileUploadMessages !== null
})
const fileMessageType = computed(() => {
  return ideStore.isFileUploadMessages?.type || FILE_MESSAGE_TYPE.INFO
})
const fileMessage = computed(() => {
  return ideStore.isFileUploadMessages?.message || ''
})
const showInputFiles = computed(() => {
  return ideStore.isInputFiles && ideStore.isInputFiles.length > 0
})
const inputFiles = computed(() => {
  return ideStore.isInputFiles
})
const isCurrentMobileTab = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.INPUTFILES
})

/**
 * Gets the download url for the input file
 * @param file The file name
 * @returns The url
 */
const getUrlInput = (file: string) => {
  return `/api/projectSync/downloadItem?projectKey=1001&name=/${encodeURI(file)}&type=input`
}
/**
 * remove the file
 * @param file The file name
 */
const removeFile = (file: string) => {
  uploadService.removeFile(file)
}
/**
 * Tries to upload the file. this is required for not reloading the page
 * @param event The event
 */
const tryUploadFile = async (event: Event) => {
  event.preventDefault()
  await uploadService.tryUploadFile(event)
  inputKey.value += 1
}
</script>

<template>
  <div class="relative h-full w-full overflow-hidden">
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="Input files"
      :icon="uploadFilesIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.UPLOAD_FILES"
    />

    <div class="surface-secondary-base relative h-full w-full overflow-auto">
      <div
        v-if="!isHtml"
        :class="[
          'flex w-full flex-col gap-5 p-2',
          { 'px-4': props.tabPosition === TAB_BUTTON_POSITION.BOTTOM }
        ]"
      >
        <div
          :class="[
            'flex h-full  w-full flex-col gap-2',
            {
              'min-w-full max-w-md': isFullScreen
            },

            {
              'order-1': isCurrentMobileTab
            }
          ]"
        >
          <h6 class="text-high text-xs font-medium sm:text-sm">Upload files</h6>

          <div class="flex h-full w-full flex-col items-center rounded-lg">
            <button
              class="background-primary stroke-primary-default relative min-h-48 w-full rounded-lg border-2 border-dashed border-opacity-85 px-5 py-5 text-center"
            >
              <p class="text-low text-sm font-semibold sm:text-lg">
                Drag your file(s) here or <span class="text-high"> Browse</span> <br />
                to instantly upload them
              </p>
              <input
                ref="uploadInput"
                :key="inputKey"
                type="file"
                name="file"
                class="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0"
                @change="tryUploadFile($event)"
                @drop.prevent="tryUploadFile($event)"
                @dragover.prevent
                @dragenter.prevent
                @dragleave.prevent
              />
            </button>
          </div>
          <p
            v-if="isFileMessage"
            :class="[
              'p-xsmall',
              {
                success: fileMessageType === FILE_MESSAGE_TYPE.SUCCESS,
                error: fileMessageType === FILE_MESSAGE_TYPE.ERROR
              }
            ]"
          >
            {{ fileMessage }}
          </p>
        </div>

        <div
          :class="[
            '  flex h-full w-full  flex-col gap-2',
            {
              'min-w-full max-w-md': isFullScreen
            },

            {
              'order-2': isCurrentMobileTab
            }
          ]"
        >
          <h6 class="text-high text-xs font-medium sm:text-sm">Uploaded files</h6>
          <div class="">
            <ul v-if="showInputFiles">
              <li
                v-for="file in inputFiles"
                :key="file"
                class="background-primary my-2 flex items-baseline justify-between gap-5 rounded p-2"
              >
                <div class="flex items-center justify-center gap-x-2">
                  <FontAwesomeIcon icon="fa-file" class="icon-button-secondary h-4 w-4" />
                  <p class="text-high text-sm sm:truncate">/uploads/{{ file }}</p>
                </div>
                <div class="flex items-center gap-5">
                  <a :href="getUrlInput(file)" target="_blank">
                    <FontAwesomeIcon icon="fa-download" class="icon-button-secondary h-4 w-4" />
                  </a>
                  <button @click="removeFile(file)">
                    <FontAwesomeIcon icon="fa-trash-can" class="icon-button-secondary h-4 w-4" />
                  </button>
                </div>
              </li>
            </ul>
            <p v-else class="background-primary mb-12 rounded py-10 text-center text-sm">
              No files uploaded
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
